/**
 * "Pregunta a tus reuniones": una pregunta sobre todas las reuniones (o las del
 * proyecto abierto). Las citas abren la reunión en el momento exacto. Nada se
 * guarda: las respuestas viven mientras la página está abierta.
 */
import { useState } from 'react'
import { Link } from 'react-router'
import type { AskAllResult } from '../lib/api'
import { formatDate, formatDuration } from '../lib/format'
import { errorText, useApp, useToast } from './AppContext'
import { IconAsk } from './icons'

interface Turn extends AskAllResult {
  pregunta: string
}

const SUGERENCIAS = [
  '¿Qué tareas quedaron pendientes esta semana?',
  '¿Qué decidimos sobre el presupuesto?',
  '¿Qué temas se repiten en las últimas reuniones?'
]

/** Dentro de un proyecto: preguntas tipo informe sobre todas sus reuniones. */
const SUGERENCIAS_PROYECTO = [
  '¿En qué estado está este proyecto?',
  '¿Qué compromisos siguen pendientes y quién los tiene?',
  '¿Qué riesgos o bloqueos se han mencionado?'
]

export function AskAllPanel({ projectId }: { projectId?: string }) {
  const { api } = useApp()
  const toast = useToast()
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const [busy, setBusy] = useState(false)
  const [turns, setTurns] = useState<Turn[]>([])
  const agotadas = turns[0]?.preguntasRestantes === 0

  async function ask(pregunta: string) {
    const text = pregunta.trim()
    if (text.length < 3 || busy || agotadas) return
    setBusy(true)
    // Las últimas 3, de la más antigua a la más nueva, para preguntas de seguimiento
    const historial = turns
      .slice(0, 3)
      .reverse()
      .filter((t) => !t.sinRespuesta)
      .map((t) => ({ pregunta: t.pregunta, respuesta: t.respuesta.slice(0, 1500) }))
    try {
      const r = await api.askAll(text, projectId, historial)
      setTurns((t) => [{ pregunta: text, ...r }, ...t])
      setQ('')
    } catch (e) {
      toast(errorText(e), 'error')
    } finally {
      setBusy(false)
    }
  }

  if (!open)
    return (
      <button
        className="btn sm"
        style={{ alignSelf: 'flex-start', marginBottom: 14 }}
        onClick={() => setOpen(true)}
        aria-expanded={false}
      >
        <IconAsk /> Pregunta a {projectId ? 'las reuniones de este proyecto' : 'tus reuniones'}
      </button>
    )

  const restantes = turns[0]?.preguntasRestantes
  return (
    <section
      className="card stack"
      style={{ gap: 10, marginBottom: 14 }}
      aria-label="Pregunta a tus reuniones"
    >
      <div className="row">
        <h2 className="grow serif" style={{ fontSize: 17, margin: 0 }}>
          Pregunta a {projectId ? 'las reuniones de este proyecto' : 'tus reuniones'}
        </h2>
        {restantes !== undefined && (
          <span className="faint" style={{ fontSize: 11.5 }}>
            {restantes === 0
              ? 'Sin preguntas por hoy'
              : `${restantes} ${restantes === 1 ? 'pregunta' : 'preguntas'} más hoy`}
          </span>
        )}
        <button className="btn ghost sm" onClick={() => setOpen(false)} aria-expanded={true}>
          Cerrar
        </button>
      </div>
      <form
        className="row"
        onSubmit={(e) => {
          e.preventDefault()
          void ask(q)
        }}
      >
        <input
          className="grow"
          aria-label="Pregunta a tus reuniones"
          placeholder="¿Qué le prometimos al cliente sobre los plazos?"
          value={q}
          maxLength={500}
          onChange={(e) => setQ(e.target.value)}
          readOnly={busy}
          disabled={agotadas}
          aria-busy={busy}
          autoFocus
        />
        <button className="btn" disabled={busy || agotadas || q.trim().length < 3}>
          {busy ? 'Buscando…' : 'Preguntar'}
        </button>
      </form>
      {turns.length === 0 && (
        <div className="row wrap" style={{ gap: 6 }}>
          {(projectId ? SUGERENCIAS_PROYECTO : SUGERENCIAS).map((s) => (
            <button
              key={s}
              className="chip"
              onClick={() => void ask(s)}
              disabled={busy || agotadas}
            >
              {s}
            </button>
          ))}
        </div>
      )}
      <div className="stack" style={{ gap: 10 }} aria-live="polite">
        {turns.map((t, i) => (
          <div
            key={turns.length - i}
            className="stack"
            style={{ gap: 6, borderTop: '1px solid var(--line-soft)', paddingTop: 10 }}
          >
            <div className="faint" style={{ fontSize: 12.5 }}>
              {t.pregunta}
            </div>
            <div className={t.sinRespuesta ? 'muted' : ''}>{t.respuesta}</div>
            {t.citas.length > 0 && (
              <ul className="stack" style={{ gap: 4, margin: 0, padding: 0, listStyle: 'none' }}>
                {t.citas.map((c, j) => (
                  <li key={j} className="row" style={{ gap: 8, alignItems: 'baseline' }}>
                    <Link
                      className="ev"
                      style={{ marginLeft: 0 }}
                      to={`/r/${c.recordingId}?t=${c.inicioMs}`}
                      title={`Abrir «${c.titulo}» en ${formatDuration(c.inicioMs)}`}
                    >
                      {formatDuration(c.inicioMs)}
                    </Link>
                    <span className="grow" style={{ fontSize: 13 }}>
                      «{c.cita}»{' '}
                      <span className="faint">
                        — {c.titulo}, {formatDate(c.fechaInicio)}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <div className="faint" style={{ fontSize: 11.5 }}>
              Revisé {t.reuniones.length} {t.reuniones.length === 1 ? 'reunión' : 'reuniones'}:{' '}
              {t.reuniones.map((m) => m.titulo).join(', ')}
            </div>
          </div>
        ))}
      </div>
      <p className="faint" style={{ fontSize: 11.5, margin: 0 }}>
        Busca en lo que se dijo en tus reuniones y en sus notas. Las preguntas no se guardan.
      </p>
    </section>
  )
}
